<template>
  <BarChart
    :chart-data="data"
    :chart-options="chartOptions"
    :plugins="plugins"
  />
</template>

<script setup lang="ts">
import { Chart as ChartJS, registerables } from "chart.js"
import { Bar as BarChart } from "vue-chartjs"
import type {
  ChartOptions,
  ChartData,
  Plugin,
} from "chart.js"
import { useCssVar } from "@vueuse/core"

ChartJS.defaults.font.family = window
  .getComputedStyle(document.documentElement)
  .getPropertyValue("font-family")
ChartJS.register(...registerables)
defineProps<{
  data: ChartData<"bar", number[], Date>
}>()
const emit = defineEmits<{
  (event: "hover-datapoint", index?: number): void
}>()

const colorText = useCssVar("--color-on-background")

const chartOptions = computed<ChartOptions<"bar">>(() => ({
  maintainAspectRatio: false,
  animation: false,
  responsive: true,
  plugins: {
    legend: {
      display: false,
      position: "right",
      labels: {
        pointStyle: "rectRounded",
        usePointStyle: true,
      },
    },
    tooltip: {
      enabled: false,
      mode: "nearest",
      intersect: false,
      axis: "x",
    },
  },
  datasets: {
    bar: {
      borderRadius: 4,
    },
  },
  events: [
    "mousemove",
    "mouseout",
    "click",
    "touchstart",
    "touchmove",
    "touchend",
  ],
  scales: {
    x: {
      stacked: true,
      type: "timeseries",
      time: {
        displayFormats: {
          month: "MMM",
        },
      },
      ticks: {
        font: {
          size: 12,
        },
        textStrokeColor: `rgb(${colorText.value} / 0.7)`,
        minRotation: 0,
        maxRotation: 0,
        padding: 8,
        autoSkipPadding: 8,
        includeBounds: false,
      },
      grid: {
        color: `rgb(${colorText.value} / 0.05)`,
        drawTicks: false,
      },
    },
    y: {
      stacked: true,
      display: false,
      beginAtZero: true,
    },
  },
}))

const plugins: Plugin<"bar">[] = [
  {
    id: "hoverEmitIndex",
    afterDatasetDraw: (chart_) => {
      const activeElements =
        chart_?.tooltip?.getActiveElements()
      if (activeElements && activeElements.length > 0) {
        const activePoint = activeElements[0]

        emit("hover-datapoint", activePoint.index)
      } else {
        emit("hover-datapoint")
      }
    },
    afterEvent: (chart, { event }) => {
      if (event.native?.type === "touchend") {
        chart.tooltip?.setActiveElements([], { x: 0, y: 0 })
        chart.setActiveElements([])
        chart.update()
      }
    },
  },
]
</script>
